import {
  listContextQuery,
  parseListContext,
  useAwaitingFirstSync,
  useDebounced,
  useListContextLabel,
  useRecipeSummaries,
} from '@platewell/query';
import { Link, useNavigate } from '@tanstack/react-router';
import { AddRecipeOptions } from '../components/add-recipe-options.js';
import { FirstSyncEmpty } from '../components/first-sync-empty.js';
import { RecipeCard } from '../components/recipe-card.js';
import { indexRoute } from '../router.js';

export function RecipeList() {
  const { q, tag } = indexRoute.useSearch();
  const navigate = useNavigate();
  const term = useDebounced(q ?? '', 150);
  // The debounced term drives what is on screen; the raw one is what a card link carries, so
  // clicking mid-keystroke lands on a recipe whose context matches the URL exactly.
  const context = parseListContext({ q: term, tag }) ?? { kind: 'all' as const };
  const linkContext = parseListContext({ q, tag }) ?? { kind: 'all' as const };
  const recipes = useRecipeSummaries(listContextQuery(context));
  const awaitingFirstSync = useAwaitingFirstSync() && recipes.length === 0;

  const heading = useListContextLabel(context) ?? 'All Recipes';

  return (
    <>
      <div className="row" style={{ marginBottom: 18 }}>
        <h1>{heading}</h1>
        <span className="spacer" />
        {!awaitingFirstSync && <span className="hint list-count">{recipes.length} recipes</span>}
        {tag != null && (
          <button className="btn ghost" onClick={() => void navigate({ to: '/', search: {} })}>
            Clear filter
          </button>
        )}
      </div>

      {awaitingFirstSync ? (
        <FirstSyncEmpty />
      ) : recipes.length === 0 && context.kind === 'all' ? (
        // An empty book, not an empty search: offer the same ways in as an empty collection.
        <div className="editor" style={{ maxWidth: 560 }}>
          <p className="hint">
            Add your first recipes; you can easily import them by photo, file or URL. They&apos;ll be
            available on every device to every member of your household, even when you&apos;re offline.
          </p>
          <AddRecipeOptions />
        </div>
      ) : recipes.length === 0 ? (
        <div className="empty">
          <h2>Nothing here yet</h2>
          <p className="hint">No recipes match. Try a different word or clear the filter. <Link className="empty-link" to="/recipes/new">Or add a new recipe</Link>.</p>
        </div>
      ) : (
        <div className="grid">
          {recipes.map((recipe) => (
            <RecipeCard key={recipe.id} recipe={recipe} context={linkContext} />
          ))}
        </div>
      )}
    </>
  );
}
